/** Screens that only tell the participant something, so there is no answer to change */
const UNANSWERABLE_SCREEN_TYPES = ['instruction', 'completion'];
/** A step's own answer feeds the wording of later questions through {token} placeholders */
const PLACEHOLDER = /\{(\w+)\}/;
const PLACEHOLDERS = /\{(\w+)\}/g;
/**
 * Question text is authored for the participant app, which renders it as html. A data change
 * request shows it as text, so entities have to be decoded and markup turned into whitespace
 * here or the reviewer reads "&nbsp;" and "</span>" in a screen title.
 */
export const normalizeQuestionText = (text) => {
    if (!text) {
        return '';
    }
    return (String(text)
        .replace(/&nbsp;/gi, ' ')
        .replace(/&#160;/g, ' ')
        .replace(/&amp;/gi, '&')
        .replace(/&lt;/gi, '<')
        .replace(/&gt;/gi, '>')
        .replace(/&quot;/gi, '"')
        .replace(/&#39;|&apos;/gi, "'")
        // Block level markup becomes a line break so a multi part question stays readable
        .replace(/<br\s*\/?>/gi, '\n')
        .replace(/<\/(p|div|li|h[1-6]|tr)>/gi, '\n')
        .replace(/<(p|div|li|h[1-6])(?:\s[^>]*)?>/gi, '\n')
        // Anything left becomes a space, so "foo</span>bar" does not read as "foobar"
        .replace(/<[^>]+>/g, ' ')
        .replace(/[ \t]+\n/g, '\n')
        .replace(/\n[ \t]+/g, '\n')
        .replace(/\n{3,}/g, '\n\n')
        .replace(/[ \t]{2,}/g, ' ')
        .trim());
};
/**
 * A screen as the site refers to it: the question the participant answered, numbered by the order
 * it appears in the activity. Screens are titled by code in the activity builder, which means
 * nothing to the person raising the request, so the code is only a fallback.
 */
export const dcrScreenLabel = (screen, withScreenCode = false) => {
    const order = screen.order !== undefined && screen.order !== null && screen.order !== '' ? String(screen.order) : '';
    const code = screen.name || screen.screen || '';
    const question = normalizeQuestionText(screen.question);
    const title = question || code;
    // Two screens can ask the same question, and then the code is the only thing telling them apart
    const suffix = withScreenCode && question && code && question !== code ? ` (${code})` : '';
    return order ? `${order}. ${title}${suffix}` : `${title}${suffix}`;
};
/**
 * A screen nobody can be asked about: it carries no answer, the participant was never asked it, or
 * the study stopped asking it.
 */
export const isSelectableDcrScreen = (screen) => !!screen &&
    !screen.disabled &&
    screen.editable !== false &&
    !!screen.stepResponseId &&
    !UNANSWERABLE_SCREEN_TYPES.includes(String(screen.type ?? '').toLowerCase());
export const selectableDcrScreens = (screens) => screens.filter(isSelectableDcrScreen);
/**
 * Titles for every screen, keyed by step id. Built for the whole activity at once because a
 * question only needs its screen code alongside it when another screen asks the same thing.
 */
export const dcrScreenLabels = (screens) => {
    const titleOf = (screen) => (normalizeQuestionText(screen.question) || screen.name || '').toLowerCase();
    const shared = screens.reduce((counts, screen) => {
        const title = titleOf(screen);
        counts[title] = (counts[title] ?? 0) + 1;
        return counts;
    }, {});
    return screens.reduce((labels, screen) => {
        labels[String(screen.stepId)] = dcrScreenLabel(screen, (shared[titleOf(screen)] ?? 0) > 1);
        return labels;
    }, {});
};
const hasValue = (value) => value !== undefined && value !== null && value !== '' && !(Array.isArray(value) && value.length === 0);
/** The answer on record for a screen. A screen the participant never reached reads as skipped */
export const dcrScreenOriginalValue = (screen) => {
    if (!screen) {
        return '';
    }
    if (screen.skipped) {
        return 'Skipped';
    }
    // A choice answer reads as the site reads it: the recorded code resolves to the site-locale
    // label, so a participant-locale label never stands next to the site-locale desired value
    // reading as a change of its own
    if (screen.choices?.length && hasValue(screen.originalChoiceValue)) {
        const codes = Array.isArray(screen.originalChoiceValue) ? screen.originalChoiceValue : [screen.originalChoiceValue];
        const labels = codes.map((code) => screen.choices?.find((choice) => String(choice.value ?? choice.c_value ?? '') === String(code))?.label);
        if (labels.every((label) => !!label)) {
            return labels.join(', ');
        }
    }
    const value = Array.isArray(screen.originalValue) ? screen.originalValue.join(', ') : screen.originalValue;
    return value === undefined || value === null || value === '' ? '' : String(value);
};
const asComparable = (value) => hasValue(value) ? (Array.isArray(value) ? value : [value]).map(String) : [];
const overlaps = (left, right) => {
    const rightValues = asComparable(right);
    return asComparable(left).some((value) => rightValues.includes(value));
};
/** An answer worded as the participant saw it, so a question quoting it reads the same way */
const answerDisplay = (value, step) => {
    if (!hasValue(value)) {
        return '';
    }
    return (Array.isArray(value) ? value : [value])
        .map((item) => {
        const option = step.options.find((candidate) => String(candidate.value) === String(item) || candidate.label === item);
        return option?.label ?? String(item);
    })
        .join(', ');
};
const currentAnswer = (step, selections) => Object.prototype.hasOwnProperty.call(selections, String(step.stepId))
    ? selections[String(step.stepId)]
    : step.originalValue;
const stepBehindValue = (steps, value) => steps.find((step) => overlaps(step.originalValue, value) ||
    step.options.some((option) => overlaps(option.value, value) || overlaps(option.label, value)));
/**
 * The activity records each variable as it stood when the participant finished, either as one
 * display string or as a list of the answers that built it.
 */
const isAnswerList = (data) => Array.isArray(data) &&
    data.length > 0 &&
    typeof data[0] === 'object' &&
    data[0] !== null &&
    'value' in data[0] &&
    'labels' in data[0];
/**
 * Rebuild a list variable from the answers currently selected: keep the parts that belong to no
 * step, such as a date, replace the parts that do, and drop the ones whose step is no longer on
 * the path. Answers the recorded snapshot never saw are appended.
 */
const rebuildAnswerList = (data, { priorSteps, allSteps, selections }) => {
    const parts = [];
    const used = new Set();
    Object.values(data[0]?.labels ?? {}).forEach((label) => {
        if (!label?.display) {
            return;
        }
        const step = stepBehindValue(allSteps, label.value);
        if (!step) {
            parts.push(String(label.display));
            return;
        }
        used.add(String(step.stepId));
        const display = answerDisplay(currentAnswer(step, selections), step);
        if (display) {
            parts.push(display);
        }
    });
    priorSteps.forEach((step) => {
        if (used.has(String(step.stepId))) {
            return;
        }
        const display = answerDisplay(currentAnswer(step, selections), step);
        if (display) {
            parts.push(display);
            used.add(String(step.stepId));
        }
    });
    return parts.join(', ') || undefined;
};
/**
 * A variable recorded as a single display string can still be a summary of several answers. Only
 * the parts that match a step are replaced, so a date or other fixed part survives the rebuild.
 */
const rebuildDisplayString = (data, { priorSteps, allSteps, selections }) => {
    const recorded = data.display === undefined || data.display === null ? '' : String(data.display);
    const current = priorSteps.map((step) => answerDisplay(currentAnswer(step, selections), step)).filter(Boolean);
    if (!recorded.includes(', ') || current.length === 0) {
        const step = stepBehindValue(allSteps, data.value);
        return step ? answerDisplay(currentAnswer(step, selections), step) || recorded : recorded || undefined;
    }
    const fixedParts = allSteps
        .reduce((remainder, step) => {
        const recordedDisplay = answerDisplay(step.originalValue, step);
        return recordedDisplay && remainder.includes(recordedDisplay)
            ? remainder.split(recordedDisplay).join(',')
            : remainder;
    }, recorded)
        .split(',')
        .map((part) => part.trim())
        .filter(Boolean);
    return [...fixedParts, ...current].join(', ');
};
const rebuildVariable = (data, context) => {
    if (data === undefined || data === null) {
        return undefined;
    }
    if (isAnswerList(data)) {
        return rebuildAnswerList(data, context);
    }
    if (Array.isArray(data)) {
        return (data
            .map((item) => item?.display)
            .filter(Boolean)
            .join(', ') || undefined);
    }
    if (typeof data === 'object' && 'display' in data) {
        return rebuildDisplayString(data, context);
    }
    if (typeof data === 'string' || typeof data === 'number') {
        return String(data);
    }
    return undefined;
};
/**
 * Fill a question's {token} placeholders from the answers currently selected. The connector
 * resolves them from what the participant answered, which stops being true the moment one of
 * those answers is the thing being changed. Tokens with nothing to fill them are left as they are.
 */
export const resolveDcrQuestionText = (rawQuestion, variables, context) => {
    if (!rawQuestion || !variables || !PLACEHOLDER.test(rawQuestion)) {
        return rawQuestion ?? '';
    }
    return rawQuestion.replace(PLACEHOLDERS, (token, key) => {
        const rebuilt = rebuildVariable(variables[key], context);
        return rebuilt ? rebuilt : token;
    });
};
const screenFor = (step, screens) => screens.find((screen) => String(screen.stepId) === String(step.stepId) || String(screen.stepResponseId) === String(step.stepResponseId));
/** Screen titles carry the order they appear in, e.g. "4. Which side was injected:" */
const orderOf = (step, screen) => {
    if (screen?.order !== undefined) {
        return Number(screen.order);
    }
    return parseInt(String(step.screen).split('.')[0], 10) || 0;
};
const toAnsweredStep = (step, screen) => ({
    stepId: String(step.stepId),
    order: orderOf(step, screen),
    originalValue: step.originalValue,
    options: step.desiredValue?.options ?? [],
});
const screenToAnsweredStep = (screen) => ({
    stepId: String(screen.stepId),
    order: Number(screen.order ?? 0),
    originalValue: screen.originalValue,
    options: (screen.choices ?? []).map((choice) => ({
        value: String(choice.value ?? choice.c_value ?? ''),
        label: String(choice.label ?? choice.c_label ?? ''),
    })),
});
/**
 * The title to show above a branching step. Questions that quote an earlier answer are rebuilt
 * from the answers currently selected; everything else keeps the title the connector resolved.
 */
export const dcrBranchingStepLabel = (step, { screens, path, steps, selections, labels }) => {
    const screen = screenFor(step, screens);
    const rawQuestion = normalizeQuestionText(screen?.rawQuestion ?? step.rawQuestion);
    const variables = (screen?.variables ?? step.variables);
    const order = orderOf(step, screen);
    if (rawQuestion && variables && PLACEHOLDER.test(rawQuestion)) {
        const answered = new Set(steps.map((candidate) => String(candidate.stepId)));
        const allSteps = steps.map((candidate) => toAnsweredStep(candidate, screenFor(candidate, screens)));
        // Questions can also quote an answer from a screen outside the branching set, such as a date
        const priorSteps = [
            ...screens
                .filter((candidate) => Number(candidate.order) < order && !answered.has(String(candidate.stepId)))
                .map(screenToAnsweredStep),
            ...path
                .filter((candidate) => String(candidate.stepId) !== String(step.stepId))
                .map((candidate) => toAnsweredStep(candidate, screenFor(candidate, screens)))
                .filter((candidate) => candidate.order < order),
        ].sort((left, right) => left.order - right.order);
        const resolved = resolveDcrQuestionText(rawQuestion, variables, { priorSteps, allSteps, selections });
        // A placeholder nothing fills would be shown verbatim, so the connector's own wording wins
        if (resolved && !PLACEHOLDER.test(resolved)) {
            return order ? `${order}. ${resolved}` : resolved;
        }
    }
    return (labels?.[String(step.stepId)] ||
        (screen ? dcrScreenLabel(screen) : '') ||
        (step.question ? dcrScreenLabel({ order, question: step.question, name: step.name }) : '') ||
        normalizeQuestionText(step.screen) ||
        step.screen ||
        '');
};
/** Titles for every step on the path, so a card and its change row read the same */
export const dcrBranchingStepLabels = (context) => context.steps.reduce((labels, step) => {
    labels[String(step.stepId)] = dcrBranchingStepLabel(step, context);
    return labels;
}, {});
